import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  InputAdornment,
  LinearProgress
} from '@mui/material';
import documentoApi from '../../api/documentoApi';
import contribuenteApi from '../../api/contribuenteApi';
import logprocessoApi from '../../api/logprocessoApi';
import BackButtonDesktop from '../../components/BackButtonDesktop';
import { AuthContext } from '../../auth/AuthContext';
import { formatDate } from '../../utils/dateFormatter';

const extractErrorMessage = (error) => {
  if (!error) return 'Errore sconosciuto';
  if (error.response?.data) return JSON.stringify(error.response?.data);
  if (!error.response) return "L'elaborazione richiede più tempo del previsto. Riprova.";
  return error.message || JSON.stringify(error);
};

export default function DocumentoUploadPageDesktop() {
  const { id, contribuenteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [documentoFascicoloId, setDocumentoFascicoloId] = useState(location.state?.documentoFascicoloId);
  const fromFascicolo = !!documentoFascicoloId;
  const isNew = !id;
  const hasContribuenteFromRoute = !!contribuenteId;

  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contribuenti, setContribuenti] = useState([]);
  const [selectedContribuente, setSelectedContribuente] = useState('');
  const [countdown, setCountdown] = useState(30);
  const [progress, setProgress] = useState(100);
  /* ========================= INIT CONTRIBUENTE ========================= */
  useEffect(() => {
    if (hasContribuenteFromRoute) {
      setSelectedContribuente(Number(contribuenteId));
    } else if (id) {
      documentoApi
        .get(id)
        .then(r => setSelectedContribuente(Number(r.data.contribuenteId)))
        .catch(e => setErr(extractErrorMessage(e)));
    }
  }, [contribuenteId, id, hasContribuenteFromRoute]);

  useEffect(() => {
    if (isNew && !hasContribuenteFromRoute) {
      contribuenteApi
        .list({ page: 0, size: 100 })
        .then(r => setContribuenti(r.data.content || []))
        .catch(e => setErr(extractErrorMessage(e)));
    }
  }, [isNew, hasContribuenteFromRoute]);

  /* ========================= FILE ========================= */
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setPreviewData(null);
      setPdfPreviewUrl(null);
      setErr(null);
    }
  };

  /* ========================= PREVIEW DATI DOCUMENTO ========================= */
  const generatePreview = async () => {
    if (!fromFascicolo && !file) return;
    if (!selectedContribuente) {
      setErr('Selezionare un contribuente');
      return;
    }

    setLoadingPreview(true);
    setCountdown(30);
    setProgress(100);
    setErr(null);

    try {
      const formData = new FormData();
      if (file) formData.append('file', file);
      formData.append('contribuenteId', selectedContribuente);
      if (documentoFascicoloId) formData.append('documentoFascicoloId', documentoFascicoloId);

      const response = isNew
        ? await documentoApi.newpreviewupload(formData)
        : await documentoApi.previewupload(id, formData);

      const processoId = response.data.id;
      let completed = false;

      while (!completed) {
        await new Promise(r => setTimeout(r, 1000));

        // Aggiorna countdown e progress
        setCountdown(prev => {
          const next = prev - 1;
          setProgress((next / 30) * 100);
          return next > 0 ? next : 30;
        });

        const statusResp = await logprocessoApi.trova(processoId);
        const p = statusResp.data;
        if (p.stato === 'COMPLETED') {
          completed = true;
          if (p.esito === 'OK') {
            setPreviewData(p.testo);
          } else {
            setErr(p.anomalie || 'Errore nel parsing');
          }
        }
      }
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  /* ========================= PREVIEW PDF LOCALE ========================= */
  const handleLocalPdfPreview = () => {
    if (!file) return;
    setPdfPreviewUrl(null);
    const url = URL.createObjectURL(file);
    setPdfPreviewUrl(url);
  };

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  /* ========================= MODIFICA SCADENZE ========================= */
  const handleChangeScadenza = (index, field, value) => {
    setPreviewData((prev) => {
      const updated = { ...prev };
      const updatedScadenze = [...updated.scadenze];
      updatedScadenze[index][field] = value;
      updated.scadenze = updatedScadenze;
      return updated;
    });
  };

  /* ========================= SALVA DOCUMENTO ========================= */
  const confirmUpload = async () => {
    if (isNew && !selectedContribuente) {
      setErr('Contribuente mancante');
      return;
    }

    // Validazione scadenze: controlla che tutte le date e importi siano presenti
    const invalidScadenze = previewData?.scadenze?.some(
      s => !s.dataScadenza || !s.importo
    );
    if (invalidScadenze) {
      setErr('Tutte le scadenze devono avere data e importo validi');
      return;
    }

    setSaving(true);
    setErr(null);
    try {
      previewData.contribuenteId = selectedContribuente;

      const response = isNew
        ? await documentoApi.create(previewData, documentoFascicoloId)
        : await documentoApi.update(id, previewData, documentoFascicoloId);

      if (user.role === "CONTRIBUENTE" && (!contribuenteId || contribuenteId === 'undefined')) {
        contribuenteId = user.contribuenteId;
      }
      if (user.role === "CONTRIBUENTE" && contribuenteId)
        navigate(`/documenti/bycontribuente/${contribuenteId}`);
      else
        navigate(`/contribuenti/${selectedContribuente}`);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* ========================= RENDER ========================= */
  return (
    <Container>
      {loadingPreview && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            px: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Elaborazione documento... ({countdown}s)
          </Typography>
          <Box sx={{ width: '100%' }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        </Box>
      )}

      <BackButtonDesktop />
      <Typography variant="h5" sx={{ mb: 2 }}>
        {fromFascicolo
          ? 'Crea Scadenze da Documento Fascicolo'
          : isNew
            ? 'Crea Documento da PDF'
            : 'Aggiungi PDF al Documento'}
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Tipologie di documento caricabili in questa pagina: F24 (UNICO o RATEIZZAZIONE AVVISO BONARIO o PREDETERMINATO), RATEIZZAZIONE ADE, ROTTAMAZIONE, MULTA, ACCERTAMENTO TARI, SOLLECITO TARI, INTIMAZIONE DI PAGAMENTO SINGOLA RATA, FATTURE in entrata e in uscita. I dati estratti dai documenti PDF sono solo indicativi e servono a fornire informazioni all'utente soprattutto riguardo alle date delle scadenze fiscali da pagare, alle quali viene prestata la massima attenzione. Gli importi e i codici dei moduli di pagamento saranno presenti ed accurati nella misura in cui i file PDF caricati sono leggibili dal software OCR che li interpreta (è indispensabile una risoluzione dei PDF di almeno 300dpi, se sono scansionati). Si invita l'utente a consultare sempre il documento originale che viene caricato nel fascicolo ed allegato alle scadenze.
      </Alert>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {!fromFascicolo && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <input
            accept="application/pdf"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="file-upload">
            <Button variant="outlined" component="span">
              {file ? 'Cambia PDF' : 'Sfoglia PDF'}
            </Button>
          </label>
          {file && <Typography>{file.name}</Typography>}
        </Stack>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={generatePreview}
          disabled={loadingPreview || (!fromFascicolo && !file)}
        >
          {loadingPreview ? 'Generazione...' : 'Anteprima dati'}
        </Button>
        {file && (
          <Button
            variant="outlined"
            onClick={handleLocalPdfPreview}
          >
            Visualizza PDF
          </Button>
        )}
      </Stack>

      {pdfPreviewUrl && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Anteprima PDF</Typography>
          <iframe
            title="pdf-preview"
            src={pdfPreviewUrl}
            width="100%"
            height="500"
            style={{ border: '1px solid #ccc' }}
          />
        </Box>
      )}

      {previewData && (
        <Box sx={{ mt: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Preview Documento</Typography>
              {previewData.descrizione && <Typography>Descrizione: {previewData.descrizione}</Typography>}
              {previewData.tipo && <Typography>Tipo Documento: {previewData.tipo}</Typography>}
              {previewData.numeroDocumento && <Typography>Numero Documento: {previewData.numeroDocumento}</Typography>}
              {previewData.dataDocumento && <Typography>Data Documento: {formatDate(previewData.dataDocumento)}</Typography>}
              {previewData.enteRiscossione && <Typography>Ente: {previewData.enteRiscossione}</Typography>}
              {previewData.cfContribuente && <Typography>CF: {previewData.cfContribuente}</Typography>}
              {previewData.nomeContribuente && <Typography>Destinatario: {previewData.nomeContribuente}</Typography>}
              {previewData.indirizzoContribuente && <Typography>Indirizzo: {previewData.indirizzoContribuente}</Typography>}
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ mt: 3 }}>Scadenze</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rata</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Importo</TableCell>
                <TableCell>Codice</TableCell>
                <TableCell>QR code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewData.scadenze?.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{s.numerorata}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={s.dataScadenza || ''}
                      onChange={(e) => handleChangeScadenza(i, 'dataScadenza', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={s.importo || ''}
                      onChange={(e) => handleChangeScadenza(i, 'importo', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={s.codiceModuloPagamento || ''}
                      onChange={(e) => handleChangeScadenza(i, 'codiceModuloPagamento', e.target.value)}
                    />
                  </TableCell>
                    <TableCell>
                      <TextField
                        value={s.qrcode || ''}
                        onChange={(e) => handleChangeScadenza(i, 'qrcode', e.target.value)}
                      />
                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Button
            sx={{ mt: 2 }}
            variant="contained"
            color="success"
            onClick={confirmUpload}
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Conferma e Salva'}
          </Button>
        </Box>
      )}
    </Container>
  );
}
